/**
 * chatOrder — where a chat row sits in the transcript, and which time it shows.
 *
 * Everything sorts by createdAt except a REVISED plan card. When the owner
 * types feedback on a pending plan, the worker keeps ONE card per task and
 * updates it in place (worker.js, approval_request update-in-place) — no
 * "Cancelled" residue card. By createdAt, though, the revised plan stays where
 * the FIRST draft appeared: above the owner's request and the agent's "Got it,
 * removing French". On 2026-09-22 Aima's revision landed silently above the
 * fold, still stamped with the first draft's time, while the last thing on
 * screen was the agent's future-tense promise to update it.
 *
 * Two payload stamps from the worker fix the position:
 *   refineRequestedAt — set when the card flips to 'refining' (the owner typed
 *                       feedback). Keeps the card near the request even if the
 *                       turn ends WITHOUT re-presenting (the stuck-spinner
 *                       recovery restores 'pending' and touches nothing else).
 *   revisedAt         — set when the revised plan lands on the card. The time
 *                       the card SHOWS, and what "Plan updated" keys on.
 *
 * While the card is 'refining' it sorts LAST — under the agent's streaming
 * acknowledgement — so the "Revising your plan…" spinner is the bottom-most
 * row and then becomes the updated card in the same spot. Sort time and
 * display time are separate on purpose: the sort sentinel is not a date.
 */

export interface OrderableChatMessage {
  messageType?: string | null;
  createdAt?: string | Date | null;
  /** Set by the worker's turnCoalescer when a streamed turn finalizes. */
  endedAt?: string | Date | null;
  payload?: unknown;
}

interface RevisionStamps {
  status?: string;
  revisedAt?: string;
  refineRequestedAt?: string;
}

/** Sorts after every real timestamp. Finite, so comparator arithmetic stays sane. */
const SORT_LAST = Number.MAX_SAFE_INTEGER;

function toMs(value: string | Date | null | undefined): number {
  if (!value) return 0;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function approvalStamps(m: OrderableChatMessage): RevisionStamps | null {
  if (m.messageType !== 'approval') return null;
  if (!m.payload || typeof m.payload !== 'object') return null;
  return m.payload as RevisionStamps;
}

/**
 * The timestamp a row displays and groups under: revisedAt for a revised plan
 * card, createdAt for everything else. Always a real date (or empty).
 */
export function chatMessageDisplayTime(m: OrderableChatMessage): string | Date | null | undefined {
  const p = approvalStamps(m);
  if (p && p.revisedAt) return p.revisedAt;
  return m.createdAt;
}

/**
 * The time a row's footer shows. A streamed turn is one row that grows for
 * minutes with its footer under the whole turn, so a finished turn shows when
 * it ENDED; createdAt is only its first output. Sorting and the day grouping
 * stay on chatMessageDisplayTime: a turn crossing midnight filed under its end
 * day could put a later day's header above an earlier one.
 */
export function chatMessageFooterTime(m: OrderableChatMessage): string | Date | null | undefined {
  if (m.endedAt && toMs(m.endedAt)) return m.endedAt;
  return chatMessageDisplayTime(m);
}

interface ChatMessageCompleteEvent {
  messageId: string;
  status: 'complete' | 'interrupted';
  endedAt?: string;
}

/**
 * The live `chat_message_complete` event applied to the transcript: flip the
 * row's status and take its end time. An event from a server that predates
 * endedAt leaves the row's own value alone.
 */
export function applyChatMessageComplete<T extends { id?: string; _id?: string }>(
  rows: T[],
  data: ChatMessageCompleteEvent,
): T[] {
  return rows.map((m) => {
    if (m.id !== data.messageId && m._id !== data.messageId) return m;
    return { ...m, status: data.status, ...(data.endedAt ? { endedAt: data.endedAt } : {}) };
  });
}

/**
 * The instant a row sorts at. A refining card sorts last; a revised card at
 * its revision; a card whose refine never re-presented at the request; every
 * other row at createdAt.
 */
export function chatMessageSortTime(m: OrderableChatMessage): number {
  const p = approvalStamps(m);
  if (p) {
    if (p.status === 'refining') return SORT_LAST;
    if (p.revisedAt) return toMs(p.revisedAt);
    if (p.refineRequestedAt) return toMs(p.refineRequestedAt);
  }
  return toMs(m.createdAt);
}

/** Array.prototype.sort comparator — oldest first, refining card last. */
export function compareChatMessages(a: OrderableChatMessage, b: OrderableChatMessage): number {
  return chatMessageSortTime(a) - chatMessageSortTime(b);
}
